import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import UserSettingsService from "src/services/userSettingsService";
import { register } from "src/ui/containers/account/api";
import { makeStyles } from "tss-react/mui";

import { InputAdornment, Typography } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { showErrorToast } from "@shared/toasts/Toasts";

import { emailRegex } from "../LoginPage/LoginPage";

import RegistrationSuccess from "./RegistrationSuccess";

const useStyles = makeStyles()((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        marginBottom: theme.spacing(5),
    },
}));

type FormValues = {
    email: string;
};

type RegisterFormProps = {
    onRegistrationComplete?: (isComplete: boolean) => void;
};

const validate = (values: FormValues) => {
    const errors: Partial<FormValues> = {};

    if (!values.email) {
        errors.email = i18next.t("pages:registerPage.validation.emailRequired");
    } else if (!emailRegex.test(values.email)) {
        errors.email = i18next.t("pages:registerPage.validation.invalidEmail");
    }

    return errors;
};

const isRecentRegistrationAttempt = (email: string, timeWindowMs: number = 5 * 60 * 1000): boolean => {
    const attempts = UserSettingsService.getRegistrationAttempts();
    const attempt = attempts[email];

    if (!attempt) {
        return false;
    }

    const timeDiff = Date.now() - attempt.timestamp;
    return timeDiff < timeWindowMs;
};

const isProblematicEmailProvider = (email: string): boolean => {
    const domain = email.split("@")[1]?.toLowerCase();
    return domain === "yahoo.com" || domain === "hotmail.com";
};

const getRegistrationErrorMessage = (email: string): string => {
    const baseMessage = i18next.t("pages:registerPage.toasts.recentAttempt");

    if (isProblematicEmailProvider(email)) {
        return i18next.t("pages:registerPage.toasts.recentAttemptWithProvider");
    }

    return baseMessage;
};

const RegisterForm = ({ onRegistrationComplete }: RegisterFormProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState("");

    const onSubmit = async (values: FormValues) => {
        try {
            if (isRecentRegistrationAttempt(values.email)) {
                showErrorToast(getRegistrationErrorMessage(values.email));
                return;
            }

            const response = await register(values.email);
            if (response.status === 200) {
                UserSettingsService.addRegistrationAttempt(values.email);

                const { needEmailConfirmation, id, token } = response.data.model;
                if (!needEmailConfirmation) {
                    const url =
                        "/account/confirm?id=" +
                        encodeURIComponent(id) +
                        "&token=" +
                        encodeURIComponent(token) +
                        "&email=" +
                        encodeURIComponent(values.email);
                    window.location.href = url;
                }

                setRegisteredEmail(values.email);
                setIsRegistrationComplete(true);
                onRegistrationComplete?.(true);
            } else if (response.status === 201) {
                UserSettingsService.addRegistrationAttempt(values.email);
                window.location.href = response.headers.location;
            }
        } catch (error: unknown) {
            const message = (error as { message?: string }).message || t("common:toast.generalError");
            showErrorToast(message);
        }
    };

    if (isRegistrationComplete) {
        return <RegistrationSuccess email={registeredEmail} />;
    }

    return (
        <>
            <div className={classes.titleContainer}>
                <Typography variant="h5">{t("pages:registerPage.title")}</Typography>
            </div>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                validateOnBlur={false}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <div className={classes.formContainer}>
                            <TextField
                                name="email"
                                type="email"
                                placeholder={t("pages:registerPage.emailPlaceholder")}
                                showErrorOnSubmit
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="fas fa-fw fa-envelope" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <PrimaryButton type="submit" disabled={submitting} fullWidth>
                                {t("common:button.submit")}
                            </PrimaryButton>
                        </div>
                    </form>
                )}
            />
        </>
    );
};

export default RegisterForm;
